/**
 * Animation Interpolators
 * 
 * Spring value calculation for scalar values (opacity, etc.)
 */

import type { AnimationProfile } from './profiles.js';


/**
 * Spring value calculation (for opacity and other scalar values)
 */
export interface SpringResult {
  pos: number;
  vel: number;
}

/**
 * Calculate spring physics for a scalar value
 */
export function springValue(
  pos: number,
  vel: number,
  target: number,
  profile: AnimationProfile
): SpringResult {
  switch (profile.type) {
    case 'spring': {
      const stiffness = profile.stiffness || 0.18;
      const damping = profile.damping || 0.95;
      let a = (target - pos) * stiffness;
      let v = (vel + a) * damping;
      let p = pos + v;
      if (Math.abs(target - p) < 0.01 && Math.abs(v) < 0.01) {
        p = target;
        v = 0;
      }
      return { pos: p, vel: v };
    }
    case 'easeInOut': {
      const speed = profile.speed || 0.18;
      const dist = target - pos;
      if (Math.abs(dist) < 0.01) return { pos: target, vel: 0 };
      const normalizedDist = Math.abs(dist);
      const easeFactor = Math.min(1, normalizedDist / 100);
      const easedSpeed = speed * (0.3 + 0.7 * easeFactor);
      const move = dist * easedSpeed;
      const newPos = pos + move;
      return { pos: newPos, vel: move };
    }
    case 'exponentialDecay': {
      const speed = profile.speed || 0.15;
      const dist = target - pos;
      if (Math.abs(dist) < 0.01) return { pos: target, vel: 0 };
      const newPos = pos + dist * speed;
      return { pos: newPos, vel: newPos - pos };
    }
    case 'linear': {
      const speed = profile.speed || 0.35;
      const dist = target - pos;
      if (Math.abs(dist) < 0.01) return { pos: target, vel: 0 };
      const move = dist * speed;
      const newPos = pos + move;
      return { pos: newPos, vel: move };
    }
    default: {
      const stiffness = profile.stiffness || 0.18;
      const damping = profile.damping || 0.95;
      let a = (target - pos) * stiffness;
      let v = (vel + a) * damping;
      let p = pos + v;
      if (Math.abs(target - p) < 0.01 && Math.abs(v) < 0.01) {
        p = target;
        v = 0;
      }
      return { pos: p, vel: v };
    }
  }
}

